import { useEffect, useMemo, useState } from 'react'
import { Select, Text } from '@nimbus-ds/components'
import { customEventsService } from '@/api/customEvents'
import { authService } from '@/api/auth'

/** An event captured in the visual editor during this session, not yet persisted. */
export interface CapturedEventOption {
  eventName?: string
  displayName?: string
  suggestedType?: string
  isPrimaryMetric?: boolean
}

export interface GoalEventOption {
  eventName: string
  label: string
  /** 'captured' = created with this experiment; 'existing' = already active on the store. */
  origin: 'captured' | 'existing'
  interactionType?: string
}

/**
 * Options for a custom_event goal: events captured in the editor for this experiment, plus the
 * store's active definitions.
 *
 * Exported for tests. The dedup matters — an event captured now will exist on the store the moment
 * the experiment is created, and showing it twice invites picking the wrong copy.
 */
export function buildGoalOptions(
  captured: CapturedEventOption[] | undefined,
  existing: Array<{ event_name?: string; display_name?: string; tracking_config?: any }> | undefined
): GoalEventOption[] {
  const options: GoalEventOption[] = []
  const seen = new Set<string>()

  for (const c of captured || []) {
    if (!c?.eventName || seen.has(c.eventName)) continue
    seen.add(c.eventName)
    options.push({
      eventName: c.eventName,
      label: c.displayName || c.eventName,
      origin: 'captured',
      interactionType: c.suggestedType,
    })
  }

  for (const e of existing || []) {
    if (!e?.event_name || seen.has(e.event_name)) continue
    seen.add(e.event_name)
    options.push({
      eventName: e.event_name,
      label: e.display_name || e.event_name,
      origin: 'existing',
      interactionType: e.tracking_config?.auto_config?.interaction_type,
    })
  }

  return options
}

/**
 * Which captured event should become the experiment's primary metric.
 *
 * The editor's panel marks one; nothing else guesses. When several are marked (shouldn't happen,
 * but captures are appended freely) the first wins, and when none is marked the goal is left alone
 * — silently binding an arbitrary event would be worse than the consultant choosing.
 */
export function primaryFromCaptured(captured: CapturedEventOption[] | undefined): CapturedEventOption | null {
  return (captured || []).find((c) => c?.isPrimaryMetric && c.eventName) || null
}

interface Props {
  value: string
  onChange: (eventName: string, label: string) => void
  /** Events captured in the visual editor for this experiment (variantConfig.captured_events). */
  capturedEvents?: CapturedEventOption[]
}

/**
 * Pick the custom event a goal measures.
 *
 * Replaces a free-text field. That field is how an experiment came to name `banner_click` when the
 * store only had `banner_clicked`: nothing validated the name, `goal_metrics` counted zero forever,
 * and the custom-events panel cheerfully counted the real events beside it. A goal can now only
 * name something that exists — or is about to, for an event captured alongside the experiment.
 */
export function CustomEventGoalPicker({ value, onChange, capturedEvents }: Props) {
  const [existing, setExisting] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) {
      setLoading(false)
      return
    }
    customEventsService
      .list(storeId, 'active')
      .then((res: any) => setExisting(res?.custom_events || []))
      .catch(() => setExisting([]))
      .finally(() => setLoading(false))
  }, [])

  const options = useMemo(
    () => buildGoalOptions(capturedEvents, existing),
    [capturedEvents, existing]
  )

  // A goal pointing at something that no longer exists must be visible, not silently blank —
  // that is the failure this component exists to end.
  const valueMissing = !!value && !options.some((o) => o.eventName === value)

  if (loading) return <Text fontSize="caption">Cargando eventos…</Text>

  if (options.length === 0) {
    return (
      <Text fontSize="caption">
        Esta tienda todavía no tiene eventos. Creá uno desde el editor visual marcando el elemento
        que querés medir.
      </Text>
    )
  }

  return (
    <div className="space-y-1">
      <Select
        id="goal-custom-event"
        name="goal-custom-event"
        value={value || ''}
        onChange={(e: any) => {
          const picked = options.find((o) => o.eventName === e.target.value)
          if (picked) onChange(picked.eventName, picked.label)
        }}
      >
        <Select.Option value="" label="Elegí un evento…" />
        {options.map((o) => (
          <Select.Option
            key={o.eventName}
            value={o.eventName}
            label={
              `${o.label}` +
              (o.interactionType ? ` · ${INTERACTION_LABEL[o.interactionType] || o.interactionType}` : '') +
              (o.origin === 'captured' ? ' · nuevo en este experimento' : '')
            }
          />
        ))}
      </Select>
      {valueMissing && (
        <Text fontSize="caption">
          ⚠ Este experimento apunta a «{value}», que no existe en la tienda. Mientras siga así, la
          métrica principal cuenta cero.
        </Text>
      )}
    </div>
  )
}

const INTERACTION_LABEL: Record<string, string> = {
  click: 'clics',
  visibility: 'vistas',
  media_view: 'imagen vista',
  scroll: 'scroll',
  form_change: 'formulario',
}
